import ApiWithLogging from '../utils/apiWithLogging';

export interface RentalExtension {
  id: number;
  rental_id: number;
  extension_number: number;
  original_end_date: string;
  // backend ส่งฟิลด์เงินเป็น Decimal ซึ่ง serialize ออกมาเป็น string ("3000.00") ไม่ใช่ number
  // → ผู้ใช้งานต้อง Number() ก่อนเปรียบเทียบ/คำนวณเสมอ (string compare ให้ผลผิด)
  // BUG-PG-EXTFEE-ZEROTOTAL: null ใน new_rental_fee = คำขอไม่ได้ขอเปลี่ยนค่าเช่า
  original_rental_fee?: number | string | null;
  new_end_date: string;
  new_rental_fee?: number | string | null;
  extension_days: number;
  extension_date: string;
  reason?: string;
  notes?: string;
  // BUG-S122-EXTSTATE: tri-state จาก backend — null = รออนุมัติ · true = อนุมัติแล้ว · false = ถูกปฏิเสธ
  is_approved?: boolean | null;
  reject_reason?: string | null;
  requested_by_user_id?: number;
  approved_by_user_id?: number;
  approved_at?: string;
  created_at: string;
  requested_by?: {
    id: number;
    username: string;
    full_name?: string;
  };
  approved_by?: {
    id: number;
    username: string;
    full_name?: string;
  };
}

export interface RentalExtensionCreate {
  rental_id: number;
  extension_days: number;
  new_rental_fee?: number;
  reason?: string;
  notes?: string;
}

export interface RentalExtensionHistory {
  rental_id: number;
  rental_number: string;
  extensions: RentalExtension[];
  total_extensions: number;
  total_extension_days: number;
  original_start_date: string;
  current_end_date: string;
}

export interface CanExtendResponse {
  can_extend: boolean;
  message: string;
  days_left?: number;
  extension_count: number;
}

class RentalExtensionService {
  // สร้างการต่อเช่า
  async createExtension(
    rentalId: number, 
    data: Omit<RentalExtensionCreate, 'rental_id'>,
    autoApprove: boolean = false
  ): Promise<RentalExtension> {
    const response = await ApiWithLogging.post<RentalExtension>(
      `/rentals/${rentalId}/extend`,
      { ...data, rental_id: rentalId },
      { params: { auto_approve: autoApprove } },
      {
        service: 'rentalExtensionService',
        action: 'create_extension',
        entityType: 'rental_extension',
        entityId: rentalId
      }
    );
    return response;
  }

  // ดึงประวัติการต่อเช่า
  async getExtensions(rentalId: number): Promise<RentalExtension[]> {
    const response = await ApiWithLogging.get<RentalExtension[]>(
      `/rentals/${rentalId}/extensions`,
      {},
      {
        service: 'rentalExtensionService',
        action: 'get_extensions',
        entityType: 'rental_extension',
        entityId: rentalId
      }
    );
    return response;
  }

  // ดึงประวัติการต่อเช่าแบบสรุป
  async getExtensionHistory(rentalId: number): Promise<RentalExtensionHistory> {
    const response = await ApiWithLogging.get<RentalExtensionHistory>(
      `/rentals/${rentalId}/extension-history`,
      {},
      {
        service: 'rentalExtensionService',
        action: 'get_extension_history',
        entityType: 'rental_extension',
        entityId: rentalId
      }
    );
    return response;
  }

  // อนุมัติ/ปฏิเสธการต่อเช่า
  // S122-EXTQUEUE (PO 2026-08-19): backend PUT /extensions/{id}/approve รองรับ
  // {approved: false, reject_reason} อยู่แล้ว — เปิดใช้จาก FE เพื่อหน้าคิวอนุมัติ
  async approveExtension(
    extensionId: number,
    approved: boolean = true,
    rejectReason?: string
  ): Promise<RentalExtension> {
    const response = await ApiWithLogging.put<RentalExtension>(
      `/rentals/extensions/${extensionId}/approve`,
      { approved, ...(rejectReason ? { reject_reason: rejectReason } : {}) },
      {},
      {
        service: 'rentalExtensionService',
        action: approved ? 'approve_extension' : 'reject_extension',
        entityType: 'rental_extension',
        entityId: extensionId
      }
    );
    return response;
  }

  // ดึงรายการที่รออนุมัติ
  async getPendingExtensions(params?: {
    skip?: number;
    limit?: number;
  }): Promise<{
    items: RentalExtension[];
    total: number;
    skip: number;
    limit: number;
  }> {
    const response = await ApiWithLogging.get<{
      items: RentalExtension[];
      total: number;
      skip: number;
      limit: number;
    }>('/rentals/extensions/pending', { params }, {
      service: 'rentalExtensionService',
      action: 'get_pending_extensions',
      entityType: 'rental_extension'
    });
    return response;
  }

  // ตรวจสอบว่าสามารถต่อได้หรือไม่
  async canExtend(rentalId: number): Promise<CanExtendResponse> {
    const response = await ApiWithLogging.get<CanExtendResponse>(
      `/rentals/${rentalId}/can-extend`,
      {},
      {
        service: 'rentalExtensionService',
        action: 'check_can_extend',
        entityType: 'rental_extension',
        entityId: rentalId
      }
    );
    return response;
  }
}

export default new RentalExtensionService();